// =============================================================================
// features/dashboard/components/DailySeriesChart.tsx — Atendimentos por dia.
//
// Atende o pedido mais explícito da operação (diagnóstico D3): "ver por dia,
// durante a semana, durante um mês". Nenhuma tela expunha série temporal — o
// dado existia por dia no banco e era descartado na agregação do endpoint.
//
// Duas leituras da MESMA série, alternáveis:
//   Por dia          — a linha do tempo do período.
//   Por dia da semana — a "base semanal" que a gestão cobra (total e média por
//                       segunda, terça, …), derivada no cliente. Derivar aqui em
//                       vez de somar no servidor evita uma segunda fonte de
//                       verdade que pode divergir da primeira.
//
// SVG manual, sem dependência externa — mesmo padrão dos gráficos existentes.
// LGPD: só contagens agregadas.
// =============================================================================

import * as React from 'react';

import type { DailySeriesItem } from '../../../hooks/dashboard/types';
import {
  bucketAverage,
  fmtAvg,
  fmtNumber as fmt,
  toDailyBuckets,
  toWeekdayBuckets,
} from '../utils/metrics';

interface DailySeriesChartProps {
  data: DailySeriesItem[];
  /** Rótulo do período selecionado, exibido no subtítulo. */
  rangeLabel: string;
  /**
   * Nome da grandeza plotada. Default "Atendimentos".
   *
   * O componente é usado nas duas telas: no dashboard o valor é interação
   * registrada; em /relatorios é conversa. Um rótulo genérico faria o gráfico
   * mentir em uma das duas.
   */
  metricLabel?: string;
  /** Nome da unidade no tooltip e no subtítulo. Default "interações". */
  unitLabel?: string;
}

type ViewMode = 'byDay' | 'byWeekday';

/**
 * Gráfico de colunas da série do período, com alternância dia × dia da semana.
 */
export function DailySeriesChart({
  data,
  rangeLabel,
  metricLabel = 'Atendimentos',
  unitLabel = 'interações',
}: DailySeriesChartProps): React.JSX.Element {
  const [mode, setMode] = React.useState<ViewMode>('byDay');

  const buckets = React.useMemo(
    () => (mode === 'byDay' ? toDailyBuckets(data) : toWeekdayBuckets(data)),
    [data, mode],
  );

  const totalInteractions = data.reduce((sum, d) => sum + d.interactions, 0);
  const maxValue = buckets.reduce((max, b) => Math.max(max, b.interactions), 0);
  const isEmpty = data.length === 0 || totalInteractions === 0;

  // Séries longas (90 dias, um ano) não cabem em rótulos legíveis: mostramos um
  // a cada N para o eixo não virar borrão. As barras continuam todas lá.
  const labelEvery = mode === 'byDay' ? Math.ceil(buckets.length / 15) : 1;

  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <p
            className="font-sans font-semibold uppercase"
            style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: 'var(--text-3)' }}
          >
            {metricLabel} por {mode === 'byDay' ? 'dia' : 'dia da semana'}
          </p>
          <p
            className="font-mono mt-1"
            style={{
              fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
              fontSize: '0.7rem',
              color: 'var(--text-4)',
            }}
          >
            {fmt(totalInteractions)} {unitLabel} — {rangeLabel}
          </p>
        </div>

        {/* Alternância de leitura */}
        <div
          className="flex rounded-sm overflow-hidden border"
          style={{ borderColor: 'var(--border-strong)' }}
          role="group"
          aria-label="Forma de agrupar a série"
        >
          {(
            [
              { value: 'byDay' as const, label: 'Por dia' },
              { value: 'byWeekday' as const, label: 'Dia da semana' },
            ] satisfies { value: ViewMode; label: string }[]
          ).map((opt) => {
            const active = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMode(opt.value)}
                aria-pressed={active}
                className="font-sans text-xs font-semibold px-3 py-1.5 transition-all duration-fast focus:outline-none focus-visible:ring-2"
                style={{
                  background: active ? 'var(--brand-azul)' : 'var(--bg-elev-1)',
                  color: active ? '#fff' : 'var(--text-3)',
                  ['--tw-ring-color' as string]: 'rgba(27,58,140,0.2)',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {isEmpty ? (
        <div
          className="flex flex-col items-center justify-center py-10 text-center"
          style={{ color: 'var(--text-3)' }}
        >
          <p className="font-sans text-sm">
            Sem {metricLabel.toLowerCase()} em {rangeLabel.toLowerCase()}.
          </p>
          <p className="font-sans text-xs mt-1" style={{ color: 'var(--text-4)' }}>
            Amplie o período para ver a evolução.
          </p>
        </div>
      ) : (
        <div className="mt-4">
          <div
            className="flex items-end gap-1 overflow-x-auto pb-1"
            style={{ height: '160px' }}
            role="img"
            aria-label={`Gráfico de colunas — ${unitLabel} por ${
              mode === 'byDay' ? 'dia' : 'dia da semana'
            } em ${rangeLabel}`}
          >
            {buckets.map((b, i) => {
              // Altura mínima de 2px quando há movimento: uma barra invisível
              // leria como "sem dado", que é diferente de "pouco movimento".
              const heightPct = maxValue > 0 ? (b.interactions / maxValue) * 100 : 0;
              const showLabel = i % labelEvery === 0;
              const avg = bucketAverage(b);

              return (
                <div
                  key={b.key}
                  className="flex flex-col items-center justify-end gap-1 flex-1"
                  style={{ minWidth: mode === 'byDay' ? '14px' : '40px', height: '100%' }}
                >
                  <div
                    className="w-full rounded-t-xs transition-all duration-slow"
                    style={{
                      height: `${Math.max(heightPct, b.interactions > 0 ? 2 : 0)}%`,
                      background: b.interactions > 0 ? 'var(--brand-azul)' : 'var(--surface-muted)',
                      minHeight: b.interactions > 0 ? '2px' : '1px',
                    }}
                    title={
                      mode === 'byDay'
                        ? `${b.fullLabel}: ${fmt(b.interactions)} ${unitLabel}, ${fmt(
                            b.leads,
                          )} leads`
                        : `${b.fullLabel}: ${fmt(b.interactions)} ${unitLabel} em ${fmt(
                            b.days,
                          )} dia(s) — média ${fmtAvg(avg)}/dia`
                    }
                  />
                  <span
                    className="font-mono select-none"
                    style={{
                      fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                      fontSize: '0.6rem',
                      color: 'var(--text-4)',
                      visibility: showLabel ? 'visible' : 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {b.label}
                  </span>
                </div>
              );
            })}
          </div>

          {mode === 'byWeekday' && (
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
              {buckets.map((b) => (
                <li key={b.key} className="flex items-baseline justify-between text-xs">
                  <span className="font-sans font-medium" style={{ color: 'var(--text-2)' }}>
                    {b.label}
                  </span>
                  <span
                    className="font-mono tabular-nums"
                    style={{
                      fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                      color: 'var(--text-3)',
                    }}
                  >
                    {fmt(b.interactions)}
                    <span className="ml-1 opacity-60">(média {fmtAvg(bucketAverage(b))})</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

export function DailySeriesChartSkeleton(): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-2)', minHeight: '240px' }}
    >
      <div
        className="mb-5 h-2.5 w-44 rounded-pill animate-pulse"
        style={{ background: 'var(--surface-muted)' }}
      />
      <div className="flex items-end gap-1" style={{ height: '160px' }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-xs animate-pulse"
            style={{
              background: 'var(--surface-muted)',
              height: `${20 + ((i * 37) % 70)}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
